import "@diligentcorp/atlas-web-component-icons/lib/menu/index.js";
import "./Logo.js";
import { msg, updateWhenLocaleChanges } from "@lit/localize";
import { css, html, LitElement } from "lit";
import { classMap } from "lit/directives/class-map.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { FORCED_DIALOG_THRESHOLD, GLOBAL_NAV_MODE_DIALOG, GLOBAL_NAV_MODE_DYNAMIC, NAV_STATE, } from "../configuration/constants.js";
import { defineComponent } from "../configuration/utils.js";
import { ResponsivePanelController } from "../controllers/ResponsivePanelController.js";
import { getLocale, setLocale } from "../localization/index.js";
import { bodyStyles, buttonDefaults, headerVar, iconButton, mediumButton, navVar, semanticVar, srOnly, statusIndicator, tertiaryButton, transition, } from "./styles.js";
const menuButtonVar = (name) => navVar(`menu-button-${name}`);
const navContainerVar = (name) => navVar(`container-${name}`);
const navHeaderVar = (name) => navVar(`header-${name}`);
/**
 * The `atlas-global-nav` component is the main entry point for the Global Navigation component.
 * It is responsible for rendering the navigation and additional content in the header.
 *
 * @tag atlas-global-nav
 * @slot leading - The content of the leading section of the header.
 * @slot trailing - The content of the trailing section of the header.
 * @slot app-switcher - The content of the app switcher section of the navigation.
 * @prop {string} locale - The locale to use for localization.
 * @prop {GlobalNavDisplayMode} displayMode - Force the navigation to always be rendered as a certain style.
 * @prop {boolean} isTrainingOrg - Display a Training Org indicator in the navigation.
 * @prop {string | undefined} launchpadUrl - The URL to the organization's launchpad.
 */
export class GlobalNav extends LitElement {
    // #region Component lifecycle methods
    constructor() {
        super();
        // References to the DOM elements that are used to render the navigation.
        this.dialogRef = createRef();
        this.navContentsRef = createRef();
        this.inlineContainerRef = createRef();
        this.panel = new ResponsivePanelController(this, window.matchMedia(`(max-width: ${FORCED_DIALOG_THRESHOLD}px)`), this.navContentsRef, this.dialogRef, this.inlineContainerRef);
        this.renderLogo = (isCompact = false) => {
            return when(this.launchpadUrl, (url) => html `
        <a href=${url}>
          <atlas-gn-diligent-logo
            class="logo transition"
            aria-hidden="true"
            .small=${isCompact}
          ></atlas-gn-diligent-logo>
          <span class="sr-only">${msg("Go to launchpad")}</span>
        </a>
      `, () => html `<atlas-gn-diligent-logo
          class="logo"
          aria-hidden="true"
          .small=${isCompact}
        ></atlas-gn-diligent-logo>`);
        };
        this.handleNavSlotChange = (e) => {
            const slot = e.target;
            const assignedElements = slot?.assignedElements?.({ flatten: true }) ?? [];
            if (assignedElements.length > 0) {
                this.panel.enable();
            }
            else {
                this.panel.disable();
            }
        };
        this.handleDialogClick = (event) => {
            if (event.target === this.dialogRef.value) {
                this._isOpen = false;
            }
        };
        this.handleDialogClose = () => {
            this._isOpen = false;
        };
        this.isTrainingOrg = false;
        this.displayMode = GLOBAL_NAV_MODE_DYNAMIC;
        this.locale = "en";
        this.launchpadUrl = undefined;
        this._isOpen = false;
        this._openModal = null;
        this.addBodyStyles();
        updateWhenLocaleChanges(this);
    }
    willUpdate() {
        if (!this.hasUpdated) {
            this._isOpen = !this.panel.isSmallScreen && this.displayMode !== GLOBAL_NAV_MODE_DIALOG;
        }
        const oldPanelState = this.panel.state;
        this.panel.updateNavState({
            displayMode: this.displayMode,
            isOpen: this._isOpen,
        });
        const newPanelState = this.panel.state;
        if (oldPanelState === "panel-expanded" &&
            newPanelState === "dialog-open" &&
            this._openModal?.open) {
            this._openModal?.close?.();
        }
        if (oldPanelState === "dialog-closed" &&
            newPanelState === "dialog-open" &&
            this._openModal?.open) {
            this._openModal?.close?.();
        }
        if (getLocale() !== this.locale) {
            setLocale(this.locale);
        }
    }
    // #endregion
    // #region Rendering functions
    render() {
        const isDialog = this.panel.isDialog;
        const isEnabled = this.panel.state !== NAV_STATE.Disabled;
        return html `
      <header class=${this.panel.state}>
        ${when(isDialog, () => this.renderMenuButton())}
        <div class="nav-container" ${ref(this.inlineContainerRef)}>${this.renderNavigation()}</div>
        ${when(isEnabled && isDialog, () => html `
            <dialog
              ${ref(this.dialogRef)}
              @click=${this.handleDialogClick}
              @close=${this.handleDialogClose}
            ></dialog>
          `)}
        <div
          class="leading"
          @atlas-gn:toggle-modal=${(e) => {
            this._openModal = e.detail;
        }}
        >
          ${this.renderLeadingSlot(isEnabled)}
        </div>
        <div class="trailing">
          ${this.renderTrailingSlotItems()}
          <slot name="trailing"></slot>
        </div>
      </header>
    `;
    }
    renderLeadingSlot(isEnabled) {
        return when(isEnabled, () => html ` <slot name="leading"></slot> `, this.renderLogo);
    }
    renderTrailingSlotItems() {
        return when(this.isTrainingOrg, () => html `
        <div class="status-indicator">
          ${msg(`Training`, { desc: "Shown in the header to indicate training org." })}
        </div>
      `);
    }
    renderNavigation() {
        const isCompact = this.panel.state === NAV_STATE.PanelCollapsed;
        return html `
      <nav
        ${ref(this.navContentsRef)}
        class=${classMap({ transition: true, compact: isCompact })}
        id="atlas-gn-global-nav"
        role="navigation"
        aria-label=${msg("Side navigation")}
      >
        <header>${this.renderMenuButton()} ${this.renderLogo(isCompact)}</header>
        <section>
          <slot name="app-switcher" @slotchange=${this.handleNavSlotChange}></slot>
        </section>
      </nav>
    `;
    }
    renderMenuButton() {
        if (this.panel.state === NAV_STATE.Disabled) {
            return;
        }
        return html `
      <button
        class="tertiary medium icon-button menu-button transition"
        @click=${() => {
            this._isOpen = !this._isOpen;
        }}
        aria-label=${this._isOpen ? msg("Collapse side navigation") : msg("Open side navigation")}
        aria-expanded=${this._isOpen ? "true" : "false"}
        aria-controls="atlas-gn-global-nav"
      >
        <dil-menu-icon aria-hidden="true"></dil-menu-icon>
      </button>
    `;
    }
    addBodyStyles() {
        const styleSheet = bodyStyles.styleSheet;
        if (styleSheet) {
            document.adoptedStyleSheets = [...document.adoptedStyleSheets, styleSheet];
        }
    }
}
/** This is used by Lit to define the reactive properties of the component. */
GlobalNav.properties = {
    locale: { type: String, reflect: true, attribute: "locale" },
    displayMode: { type: String, attribute: "display-mode" },
    isTrainingOrg: { type: Boolean, attribute: "is-training" },
    launchpadUrl: { type: String, attribute: "launchpad-url" },
    _isOpen: { state: true },
    _openModal: { state: true },
};
GlobalNav.styles = [
    buttonDefaults,
    mediumButton,
    tertiaryButton,
    iconButton,
    statusIndicator,
    transition,
    srOnly,
    css `
      :host {
        display: flex;
        color: ${semanticVar("color-type-default")};
        background-color: ${semanticVar("color-background-base")};
        font-family:
          "Plus Jakarta Sans Variable",
          -apple-system,
          BlinkMacSystemFont,
          "Segoe UI",
          Roboto,
          "Helvetica Neue",
          Arial,
          sans-serif,
          "Apple Color Emoji",
          "Segoe UI Emoji",
          "Segoe UI Symbol";
      }

      header {
        box-sizing: border-box;
        display: flex;
        width: 100%;
        max-width: 100vw;
        flex-direction: var(--header-direction, row);
        align-items: center;
        gap: ${headerVar("gap")};
        height: ${headerVar("height")};
        border-bottom-color: var(--header-border-color, ${headerVar("border-bottom-color")});
        border-bottom-width: ${headerVar("border-bottom-width")};
        border-bottom-style: ${headerVar("border-bottom-style")};
        padding-left: ${headerVar("padding-left")};
        padding-right: ${headerVar("padding-right")};
        background-color: inherit;
        color: inherit;
        transition-property: padding, height;
      }
      a:has(.logo) {
        display: inline-flex;
        color: inherit;
      }
      header > button,
      header > .logo {
        transition-property: transform;
      }

      header > nav {
        align-self: flex-start;
      }

      header.panel-collapsed nav {
        --atlas-side-nav-width: var(--atlas-side-nav-collapse-width);
        --label-display: none;
        --list-padding-left: ${navVar("collapsed-list-padding-left")};
        --list-padding-right: ${navVar("collapsed-list-padding-right")};
        --atlas-global-header-height: ${navVar("collapsed-header-height")};
      }
      header.panel-collapsed nav .logo {
        transform: translate(-41px, 54px);
      }

      header.disabled nav {
        display: none;
      }

      header .leading,
      header .trailing {
        display: flex;
        flex-direction: row;
        align-items: center;
      }

      header .leading {
        flex-grow: 1;
        padding: ${headerVar("leading-slot-padding")};
        gap: ${headerVar("leading-slot-gap")};
      }
      header .trailing {
        gap: ${headerVar("trailing-slot-gap")};
      }
      header .leading:has(.logo) {
        padding-left: ${headerVar("public-leading-slot-padding-left")};
      }

      :host > header {
        padding-left: calc(${headerVar("padding-left")} + var(--atlas-global-nav-content-left));
      }

      nav {
        display: flex;
        flex-direction: column;
        background-color: ${navContainerVar("background-color")};
        color: ${navContainerVar("color")};
        position: fixed;
        top: 0;
        left: 0;
        height: 100dvh;
        width: var(--atlas-side-nav-width);
        transition-property: width;
        --label-display: flex;
        --list-padding-left: ${navVar("expanded-list-padding-left")};
        --list-padding-right: ${navVar("expanded-list-padding-right")};
      }

      section {
        overflow: hidden;
        overscroll-behavior: contain;
        --atlas-side-nav-inset: var(--lens-core-spacing-0-25);
        margin: var(--atlas-side-nav-inset);
      }

      @media (hover: hover) {
        section {
          overflow-y: hidden;
        }
        section:hover {
          overflow-y: auto;
        }
        section::-webkit-scrollbar {
          width: var(--lens-core-spacing-0-5);
          height: var(--lens-core-spacing-0-5);
          background-color: transparent;
        }
        section::-webkit-scrollbar-thumb {
          background: ${semanticVar("color-ui-divider-secondary")};
          border-radius: ${semanticVar("radius-full")};
        }
      }
      @media (hover: none) {
        section {
          overflow-y: auto;
        }
      }

      nav > header {
        flex-shrink: 0;
        align-items: flex-start;
        justify-content: flex-start;
        background-color: ${navContainerVar("background-color")};
        padding-top: ${navHeaderVar("padding-top")};
        padding-bottom: ${navHeaderVar("padding-bottom")};
        height: var(--atlas-global-header-height);
        --header-border-color: ${navHeaderVar("border-bottom-color")};
      }

      nav > section {
        flex-grow: 1;
      }

      nav > header .logo {
        transform: translate(0, var(--lens-core-spacing-1));
      }
      nav > header button.menu-button {
        --button-background: ${menuButtonVar("default-background-color")};
        --button-color: ${menuButtonVar("default-color")};
      }
      nav > header button.menu-button:hover {
        --button-background: ${menuButtonVar("hover-background-color")};
        --button-hover-color: ${menuButtonVar("hover-color")};
      }
      nav > header button.menu-button:active {
        --button-background: ${menuButtonVar("active-background-color")};
        --button-active-color: ${menuButtonVar("active-color")};
      }
      nav > header button.menu-button:focus {
        outline-color: ${menuButtonVar("focus-outline-color")};
      }

      .nav-container {
        display: contents;
      }

      dialog {
        background-color: transparent;
        border: none;
        padding: 0;
        margin: 0;
        height: 100dvh;
        max-height: 100dvh;
        width: var(--atlas-side-nav-width);
        left: 0;
        top: 0;
        bottom: 0;
        animation-duration: var(--atlas-global-nav-animation-duration);
        animation-timing-function: var(--atlas-global-nav-easing);
        animation-name: slide-in;
        z-index: calc(Infinity);
      }
      dialog[open] {
        transform: translateX(0);
      }
      dialog::backdrop {
        background: ${navContainerVar("backdrop-background-color")};
      }

      @keyframes slide-in {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(0);
        }
      }
    `,
];
export const GLOBAL_NAV_TAG = "atlas-global-nav";
defineComponent(GLOBAL_NAV_TAG, GlobalNav);
